import { Table } from "@/components/shared/ui/RegistryTable";
import { useGetCreditIssuanceListForRegistryPagination } from "@/hooks/registry/useGetCreditIssuanceListForRegistryPagination";
import { feRoutes } from "@/routes/feRoutes";
import type { CreditIssuanceListItem } from "@/types/creditTracking";
import { useMemo } from "react";
import { FullPageLoader } from "../shared/ui/Loader";
import PaginationTwo from "../shared/ui/PaginationTwo";

const formatIssuanceDate = (isoDate: string) => isoDate.slice(0, 10);

interface Props {
	hidePagination?: boolean;
}

const CreditIssuanceTableWrapper = ({ hidePagination = false }: Props) => {
	const { creditIssuanceList, pagination } = useGetCreditIssuanceListForRegistryPagination();
	const { data: issuanceListData, isLoading } = creditIssuanceList;
	const { changeOffset } = pagination;

	const list = issuanceListData?.data ?? [];

	const handleRowClick = (row: CreditIssuanceListItem) => {
		window.open(feRoutes.registry.issuanceDetails(row.creditIssuanceId), "_blank");
	};

	const columns = useMemo(
		() => [
			{
				header: "ID",
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="min-w-[60px]">{row.row.original.formattedIssuanceId}</div>
				),
				accessorKey: "formattedIssuanceId",
			},
			{
				header: "Issuance Date",
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="min-w-[110px]">{formatIssuanceDate(row.row.original.eventDate)}</div>
				),
				accessorKey: "eventDate",
			},
			{
				header: "Project",
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="min-w-[180px] whitespace-normal break-words">{row.row.original.projectTitle}</div>
				),
				accessorKey: "projectTitle",
			},
			{
				header: "Project Developer",
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="min-w-[140px] max-w-[280px] whitespace-normal break-words">
						{row.row.original.projectOwner.legalName}
					</div>
				),
				accessorKey: "projectOwner",
			},
			{
				header: "Sectoral Scope",
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="flex min-w-[180px] flex-col gap-y-0.5 whitespace-normal break-words text-left">
						<span>{row.row.original.sectoralScope.primary}</span>
						<span className="text-gray-600">{row.row.original.sectoralScope.secondary}</span>
					</div>
				),
				accessorKey: "sectoralScope",
			},
			{
				header: () => <div className="w-full text-right">Credits</div>,
				cell: (row: { row: { original: CreditIssuanceListItem } }) => (
					<div className="min-w-[80px] text-right">{row.row.original.totalCreditCount.toLocaleString("en-US")}</div>
				),
				accessorKey: "totalCreditCount",
			},
		],
		[],
	);

	if (isLoading) return <FullPageLoader />;

	return (
		<div>
			{(!list || list.length === 0) && (
				<div className="flex justify-center py-20">
					<p>No issuances yet.</p>
				</div>
			)}

			{list && list.length > 0 && (
				<div>
					{!hidePagination && issuanceListData && issuanceListData.count > list.length && (
						<div className="mt-8 flex w-full justify-end py-0 pb-0 pr-0 md:px-8">
							<PaginationTwo
								count={issuanceListData.count}
								changeOffset={changeOffset}
								offset={parseInt(issuanceListData.offset, 10)}
								limit={parseInt(issuanceListData.limit, 10)}
							/>
						</div>
					)}
					<Table data={list} columns={columns} onRowClick={handleRowClick} />
				</div>
			)}
		</div>
	);
};

export default CreditIssuanceTableWrapper;
